import { Injectable } from '@angular/core';
import {
  BasePageMetaResolver,
  PageLinkService,
  Product,
  ProductPageMetaResolver,
  ProductService,
  RoutingService,
  TranslationService,
} from '@spartacus/core';
import { Observable, map, switchMap } from 'rxjs';

/**
 * Extends the default Spartacus ProductPageMetaResolver to override the
 * canonical URL generation.
 *
 * Problem: Products are accessible via multiple URL patterns
 * (e.g. /product/:code/:catalog/:slug and /p/:code), causing duplicate
 * content issues for SEO crawlers.
 *
 * Solution: Forces the canonical URL to always use the long format:
 *   /product/:productCode/:catalogNumber/:slug
 *
 * The Spartacus SeoModule automatically injects the resolved canonical URL
 * as a <link rel="canonical"> tag in the <head>.
 */
@Injectable({ providedIn: 'root' })
export class CustomProductPageMetaResolver extends ProductPageMetaResolver {
  constructor(
    routingService: RoutingService,
    productService: ProductService,
    translation: TranslationService,
    basePageMetaResolver: BasePageMetaResolver,
    pageLinkService: PageLinkService,
  ) {
    super(routingService, productService, translation, basePageMetaResolver, pageLinkService);
  }

  /**
   * Resolves the canonical URL for the Product Detail Page using the long
   * format: /product/:productCode/:catalogNumber/:slug
   *
   * Uses the native RoutingService.getFullUrl() to build the absolute URL
   * based on the configured 'product' route, and PageLinkService to apply
   * canonical URL normalization (https, trailing slash, etc.).
   */
  override resolveCanonicalUrl(): Observable<string> {
    return this.product$.pipe(
      switchMap((product: Product) => this.findBaseProduct(product)),
      map((product: Product) => {
        if (!product?.code) {
          return '';
        }

        const params = {
          ...product,
          catalogNumber: (product as any).catalogNumber || '',
          slug: this.buildSlug(product.name),
        };

        const url = this.routingService.getFullUrl({
          cxRoute: 'product',
          params,
        });

        return this.pageLinkService.getCanonicalUrl({}, url);
      }),
    );
  }

  private buildSlug(name?: string): string {
    if (!name) {
      return '';
    }
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }
}
